import { Box, IconButton, Modal } from "@mui/material";
import axios from "axios";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import uploadSignedImage from "utils/uploadImg";
import { loadDecrypted } from "Common Usage/localStorageUtils"; // <-- added

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 500,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
};

const AddAvatar = ({ visible, onClose, fetchAllAvatars }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    avatarUrl: "",
    gender: "",
    isPrivate: false,
  });

  const reset = () => {
    setLoading(false);
    setUploading(false);
    setError("");
    setSuccess("");
    setFormData({
      avatarUrl: "",
      gender: "",
      isPrivate: false,
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadedUrl = await uploadSignedImage(file);
      console.log("Uploaded URL============>", uploadedUrl);
      setFormData((prev) => ({
        ...prev,
        avatarUrl: uploadedUrl,
      }));
    } catch (error) {
      console.error("Image upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      let config = {
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/avatar/create`,
        headers: {
          "Content-Type": "application/json",
        },
        data: formData,
      };

      const response = await axios.request(config);
      setSuccess("Avatar added successfully");

      // ─────────────────────────────────────────────────────────
      // Create staff activity log for avatar creation
      // ─────────────────────────────────────────────────────────
      try {
        const staffData = loadDecrypted("HeartConnect-StaffData");
        const staffId = staffData?._id || staffData?.id || "";
        const staffName = staffData?.fullName || staffData?.name || "Unknown";

        if (staffId) {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
            {
              staff: staffId,
              action: "CREATE_AVATAR",
              description: `Staff ${staffName} added a new avatar (gender: ${formData.gender}, url: ${formData.avatarUrl})`,
            },
            { headers: { "Content-Type": "application/json" } }
          );
        }
      } catch (logErr) {
        console.error("Failed to create staff activity log:", logErr);
      }
      // ─────────────────────────────────────────────────────────

      fetchAllAvatars();
      reset();
      onClose();
    } catch (error) {
      console.log("Error in creating avatar: ", error);
      setError("Failed to create avatar. Please try again later");
    } finally {
      setLoading(false);
      setFormData({
        avatarUrl: "",
        gender: "",
      });
    }
  };

  return (
    <Modal open={visible} onClose={onClose}>
      <Box sx={style}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#c51369" }}
        >
          <FaTimes />
        </IconButton>
        <div className="mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="mb-6 text-center text-2xl font-bold text-navy-700">
              Add New Avatar
            </h2>
            {error && <p className="mb-4 text-center text-red-500">{error}</p>}
            {success && (
              <p className="mb-4 text-center text-green-500">{success}</p>
            )}

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="avatar"
                  className="mb-2 block text-lg text-navy-700"
                >
                  Avatar
                </label>
                <input
                  type="file"
                  id="avatar"
                  name="avatar"
                  onChange={handleFileChange}
                  accept="Image/*"
                  className="w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
                  required
                />
              </div>
              {uploading ? (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg
                    className="h-5 w-5 animate-spin text-[#c51369]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  Uploading...
                </div>
              ) : formData.avatarUrl ? (
                <img
                  src={formData.avatarUrl}
                  alt="Avatar"
                  className="h-24 w-24 rounded-2xl border object-cover"
                />
              ) : null}

              <div>
                <label
                  htmlFor="description"
                  className="mb-2 block text-lg text-navy-700"
                >
                  Applicable for
                </label>
                <select
                  name="gender"
                  id="gender"
                  className="w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
                  value={formData.gender}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      gender: e.target.value,
                    })
                  }
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPrivate"
                  name="isPrivate"
                  checked={formData.isPrivate || false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isPrivate: e.target.checked,
                    })
                  }
                  className="h-4 w-4 rounded border-gray-300 text-[#c51369] focus:ring-[#c51369]"
                />
                <label htmlFor="isPrivate" className="text-sm font-medium text-gray-700 cursor-pointer">
                  VIP / Private Avatar (Hide from regular users)
                </label>
              </div>
            </div>
            <button
              type="submit"
              className="w-full rounded-md bg-[#c51369] p-3 text-white hover:bg-[#a10f5b]"
            >
              {loading ? <BtnLoader /> : "Add Avatar"}
            </button>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default AddAvatar;




// import { Box, IconButton, Modal } from "@mui/material";
// import axios from "axios";
// import BtnLoader from "Components/Common/Loader/BtnLoader";
// import React, { useState } from "react";
// import { FaTimes } from "react-icons/fa";
// import uploadSignedImage from "utils/uploadImg";
// import uploadImg from "utils/uploadImg";

// const style = {
//   position: "absolute",
//   top: "50%",
//   left: "50%",
//   transform: "translate(-50%, -50%)",
//   width: "90%",
//   maxWidth: 500,
//   bgcolor: "background.paper",
//   border: "none",
//   borderRadius: 8,
//   boxShadow: 24,
//   p: 4,
// };

// const AddAvatar = ({ visible, onClose, fetchAllAvatars }) => {
//   const [loading, setLoading] = useState(false);
//   const [uploading, setUploading] = useState(false);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState("");
//   const [formData, setFormData] = useState({
//     avatarUrl: "",
//     gender: "",
//   });

//   const reset = () => {
//     setLoading(false);
//     setUploading(false);
//     setError("");
//     setSuccess("");
//     setFormData({
//       avatarUrl: "",
//       gender: "",
//     });
//   };

//   const handleFileChange = async (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     setUploading(true);
//     try {
//       const uploadedUrl = await uploadSignedImage(file);
//       console.log("Uploaded URL============>", uploadedUrl);
//       setFormData((prev) => ({
//         ...prev,
//         avatarUrl: uploadedUrl,
//       }));
//     } catch (error) {
//       console.error("Image upload failed:", error);
//       // Optionally handle error
//     } finally {
//       setUploading(false);
//     }
//   };
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setError("");
//     setSuccess("");

//     try {
//       let config = {
//         method: "post",
//         url: `${process.env.REACT_APP_API_URL}/avatar/create`,
//         headers: {
//           "Content-Type": "application/json",
//         },
//         data: formData,
//       };

//       const response = await axios.request(config);
//       // console.log("Response of handleSubmit: ", response.data);
//       setSuccess("Avatar added successfully");

//       fetchAllAvatars();
//       reset();
//       onClose();
//     } catch (error) {
//       console.log("Error in creating avatar: ", error);
//       setError("Failed to create avatar. Please try again later");
//     } finally {
//       setLoading(false);
//       setFormData({
//         avatarUrl: "",
//         gender: "",
//       });
//     }
//   };

//   return (
//     <Modal open={visible} onClose={onClose}>
//       <Box sx={style}>
//         <IconButton
//           onClick={onClose}
//           sx={{ position: "absolute", right: 8, top: 8, color: "#c51369" }}
//         >
//           <FaTimes />
//         </IconButton>
//         <div className="mx-auto">
//           <form onSubmit={handleSubmit} className="space-y-6">
//             <h2 className="mb-6 text-center text-2xl font-bold text-navy-700">
//               Add New Avatar
//             </h2>
//             {error && <p className="mb-4 text-center text-red-500">{error}</p>}
//             {success && (
//               <p className="mb-4 text-center text-green-500">{success}</p>
//             )}

//             <div className="space-y-4">
//               <div>
//                 <label
//                   htmlFor="avatar"
//                   className="mb-2 block text-lg text-navy-700"
//                 >
//                   Avatar
//                 </label>
//                 <input
//                   type="file"
//                   id="avatar"
//                   name="avatar"
//                   onChange={handleFileChange}
//                   accept="Image/*"
//                   className="w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
//                   required
//                 />
//               </div>
//               {uploading ? (
//                 <div className="flex items-center gap-2 text-sm text-gray-600">
//                   <svg
//                     className="h-5 w-5 animate-spin text-[#c51369]"
//                     xmlns="http://www.w3.org/2000/svg"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                   >
//                     <circle
//                       className="opacity-25"
//                       cx="12"
//                       cy="12"
//                       r="10"
//                       stroke="currentColor"
//                       strokeWidth="4"
//                     ></circle>
//                     <path
//                       className="opacity-75"
//                       fill="currentColor"
//                       d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
//                     ></path>
//                   </svg>
//                   Uploading...
//                 </div>
//               ) : formData.avatarUrl ? (
//                 <img
//                   src={formData.avatarUrl}
//                   alt="Avatar"
//                   className="h-24 w-24 rounded-2xl border object-cover"
//                 />
//               ) : null}

//               <div>
//                 <label
//                   htmlFor="description"
//                   className="mb-2 block text-lg text-navy-700"
//                 >
//                   Applicable for
//                 </label>
//                 <select
//                   name="gender"
//                   id="gender"
//                   className="w-full rounded-md border border-gray-300 p-2 focus:ring-2 focus:ring-[#c51369]"
//                   value={formData.gender}
//                   onChange={(e) =>
//                     setFormData({
//                       ...formData,
//                       gender: e.target.value,
//                     })
//                   }
//                 >
//                   <option value="">Select Gender</option>
//                   <option value="male">Male</option>
//                   <option value="female">Female</option>
//                   <option value="other">Other</option>
//                 </select>
//               </div>
//             </div>
//             <button
//               type="submit"
//               className="w-full rounded-md bg-[#c51369] p-3 text-white hover:bg-[#a10f5b]"
//             >
//               {loading ? <BtnLoader /> : "Add Avatar"}
//             </button>
//           </form>
//         </div>
//       </Box>
//     </Modal>
//   );
// };

// export default AddAvatar;
